// useSessionLifecycle — owns the concept-card handoff (acknowledge/cancel)
// for the legacy session-start path.
//
// MA-155 (retake-placement-wiring, 2026-07-21): handlePlacementStart,
// handlePlacementSkip, handleStart, handleSessionFinish, and their
// private helpers (computeSupportOverrides, startSessionWithConcept)
// were removed as dead code. None had a live caller — App.jsx stopped
// destructuring handleStart/handleSessionFinish in the MA-34 PR-1
// dead-field cleanup (docs/adr/011), and handlePlacementStart/
// handlePlacementSkip only ever served the legacy <PlacementScreen>,
// deleted in the same slice (see App.jsx's decision log: its "Start
// quick check" flow navigated into /play/session, which has no concept
// of the placement domain object and never resolves placementStatus off
// "in_progress" — a confirmed dead end, not live UX).
//
// MA-39: handleGoHome/handleStartNext (the streak/day/completedToday
// writer pair) were removed — App.jsx stopped destructuring them (and
// `completedSession`) in the MA-34 PR-1 dead-field cleanup (docs/adr/011),
// and a repo-wide grep found zero other consumers, so the pair — and the
// `syncStreakToCloud` dep it was the only caller of — were dead code, not
// live duplication. See MA-39's decision log for the fuller finding.
//
// This hook composes useActiveSession internally (so the in-flight session
// state lives next to the handlers that mutate it) and accepts the rest of
// its dependencies as an explicit dep-bag. Returns:
//   - all useActiveSession values + setters (re-exported so App can pass
//     them to screens unchanged)
//   - the concept-card handoff handlers

import { useCallback } from "react";
import { recordConceptCardShown } from "../engine/conceptCards";
import useActiveSession from "./useActiveSession.js";

/**
 * @param {object} deps
 * @param {(updater) => void} deps.setProgress
 * @param {(screen: string) => void} deps.setScreen
 */
export default function useSessionLifecycle({ setProgress, setScreen }) {
  const sess = useActiveSession();
  const {
    activeSession, completedSession, activeStorySlug, pendingConcept,
    setActiveSession, setCompletedSession, setActiveStorySlug, setPendingConcept,
    clearActive,
  } = sess;

  const handleConceptAcknowledged = useCallback(() => {
    if (!pendingConcept) return;
    const { moduleId, session } = pendingConcept;
    setProgress((prev) => ({
      ...prev,
      engineState: {
        ...(prev.engineState || {}),
        conceptCardLog: recordConceptCardShown(moduleId, prev.engineState?.conceptCardLog),
      },
    }));
    setPendingConcept(null);
    setActiveSession(session);
    setScreen("session");
  }, [pendingConcept, setProgress, setPendingConcept, setActiveSession, setScreen]);

  const cancelConcept = useCallback(() => {
    setPendingConcept(null);
    setActiveSession(null);
    setScreen("home");
  }, [setPendingConcept, setActiveSession, setScreen]);

  return {
    activeSession, completedSession, activeStorySlug, pendingConcept,
    setActiveSession, setCompletedSession, setActiveStorySlug, setPendingConcept,
    clearActive,
    handleConceptAcknowledged,
    cancelConcept,
  };
}
